import { FormControl, FormHelperText, InputLabel, useTheme } from '@mui/material';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneInput from 'react-phone-input-2';

import { LocaleContext } from '@/Contexts/LocaleContext';
import { errorIcon } from '@/utils/formInputUtil';

const isPhoneNumberValid = (phone: string): boolean => {
  const normalizedPhone = phone.trim();
  if (normalizedPhone === '') return false;

  const withCountryPrefix = normalizedPhone[0] === '+' ? normalizedPhone : `+${normalizedPhone}`;
  return isValidPhoneNumber(withCountryPrefix);
};

function PhoneNumberSelect({
  id = 'phone',
  label,
  phone,
  defaultCountry = 'us',
  countries = undefined,
  required = false,
  showError = true,
  updatePhoneNumber,
  validationFunction,
}: {
  id?: string;
  label: ReactNode;
  phone?: string | null;
  defaultCountry?: string | null; // ISO2 country code
  countries?: string[] | undefined; // if blank, all countries are shown
  required?: boolean;
  showError?: boolean;
  updatePhoneNumber?: (value: string) => void;
  validationFunction?: (value: string) => boolean; // return true if valid, false is not valid
}) {
  const theme = useTheme();
  const { translatedText } = useContext(LocaleContext);
  const [hasError, setHasError] = useState(false);
  const [countryCode, setCountryCode] = useState<string | undefined>();

  useEffect(() => {
    const phoneIsEmpty = phone == null || phone.length === 0;
    if (phoneIsEmpty && defaultCountry != null) {
      setCountryCode(defaultCountry.toLowerCase());
    } else if (phoneIsEmpty && defaultCountry == null && countryCode == null) {
      setCountryCode('us');
    }
  }, [defaultCountry, phone, countryCode]);

  useEffect(() => {
    if (!showError) {
      setHasError(false);
      return;
    }

    const normalizedPhone = phone ?? '';
    const basePhoneValid = isPhoneNumberValid(normalizedPhone);
    const customPhoneValid =
      validationFunction == null ? true : Boolean(validationFunction(normalizedPhone));
    const isPhoneValid = basePhoneValid && customPhoneValid;

    setHasError(!isPhoneValid && phone != null);
  }, [phone, showError, validationFunction]);

  const handleIsValid = useCallback(
    (value: string) => {
      if (!showError) {
        return true;
      }

      const basePhoneValid = isPhoneNumberValid(value);
      const customPhoneValid =
        validationFunction == null ? true : Boolean(validationFunction(value));
      const isPhoneValid = basePhoneValid && customPhoneValid;

      return isPhoneValid;
    },
    [showError, validationFunction]
  );

  return (
    <FormControl
      required={required}
      error={hasError}
      fullWidth
      sx={{
        '& .react-tel-input': {
          border: `1px solid ${theme.palette.sandShade3.main}`,
          borderRadius: '4px',
          width: '100%',
          fontFamily: '"sofia-pro", sans-serif',
        },
        '& .react-tel-input input.form-control': {
          border: 'none',
          height: 'initial',
          width: '100%',
          paddingLeft: '56px',
          paddingTop: '12px',
          paddingBottom: '12px',
          fontSize: '16px',
          lineHeight: '24px',
          fontFamily: '"sofia-pro", sans-serif',
          backgroundColor: theme.palette.sandTint.main,
          color: theme.palette.taiga.main,
          '::placeholder': {
            color: theme.palette.taigaTint2.main,
            opacity: 1,
          },
          ':autofill': {
            boxShadow: `0 0 0 1000px ${theme.palette.autoFill.main} inset !important`,
          },
        },
        '& .react-tel-input .flag-dropdown': {
          border: 'none',
          borderRight: `1px solid ${theme.palette.sandShade3.main}`,
          backgroundColor: theme.palette.sandTint.main,
          paddingLeft: '4px',
          paddingRight: '4px',
        },
        '& .react-tel-input .flag-dropdown:focus-within': {
          borderColor: theme.palette.ocean.main,
        },
        '& .react-tel-input .flag-dropdown.open .selected-flag, & .react-tel-input .selected-flag:hover, & .react-tel-input .selected-flag:focus':
          {
            backgroundColor: 'unset',
          },
        '& .react-tel-input:focus-within': {
          borderColor: theme.palette.ocean.main,
        },

        '& .react-tel-input:has(.invalid-number)': {
          borderColor: theme.palette.error.main,
          '::after': {
            content: errorIcon,
            width: '20px',
            height: '20px',
            position: 'absolute',
            right: '12px',
            top: '14px',
          },
        },
        '& .react-tel-input input.form-control.invalid-number': {
          border: 'none',
        },
      }}
    >
      <InputLabel htmlFor={id} sx={{ pointerEvents: 'initial' }}>
        {label}
      </InputLabel>
      <PhoneInput
        inputProps={{
          required: required,
          id: id,
        }}
        onlyCountries={countries}
        value={phone ?? ''}
        countryCodeEditable={false}
        disableCountryGuess
        disableSearchIcon
        enableSearch
        autocompleteSearch
        enableTerritories
        preferredCountries={['us']}
        placeholder=''
        country={countryCode}
        onChange={updatePhoneNumber}
        isValid={handleIsValid}
      />
      {showError && hasError && (
        <FormHelperText>{translatedText.contact.invalidPhoneNumber}</FormHelperText>
      )}
    </FormControl>
  );
}
export default PhoneNumberSelect;
